/**
 * Central helper for the Aegis-signed session access token (see AegisSessionTokenService on the
 * backend). This is DISTINCT from the external OIDC identity provider's access token: the OIDC
 * token is only ever valid at the session-mint endpoint (POST /v1/sessions); every subsequent API
 * request must carry this Aegis-signed token instead.
 *
 * SECURITY (pentest finding C01): CsrfMiddleware previously captured a Bearer token ONCE at
 * construction time and reused that same (increasingly stale) value forever. Aegis now mints a
 * fresh ~10-minute token on every session create/switch/refresh; this module is the single
 * source of truth callers use to keep the stored token in sync so every later request picks up
 * the live value (see csrf-enabled-client.ts's getCurrentToken() and lib/api/auth-headers.ts).
 *
 * Storage key ('authToken') and shape ({ access_token, token_type }) intentionally match the
 * pre-existing convention in auth.tsx / session-management.ts / lib/api/auth-headers.ts so every
 * existing reader picks up the new value with no further changes.
 */
const AUTH_TOKEN_STORAGE_KEY = 'authToken';

export interface StoredAuthToken {
  access_token: string;
  token_type: string;
}

/**
 * Persist a fresh access token as the ONE current token for all subsequent requests.
 * Writes to sessionStorage (preferred) and removes any legacy localStorage copy, matching
 * auth.tsx's setAuthToken() convention.
 */
export function persistAccessToken(accessToken: string | null | undefined): void {
  if (typeof window === 'undefined' || !accessToken) {
    return;
  }
  try {
    const payload: StoredAuthToken = { access_token: accessToken, token_type: 'Bearer' };
    sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('authTokenChanged', { detail: { authToken: payload } }));
  } catch {
    /* best-effort; a failed write here just means the next request falls back to whatever is
       already stored (or the constructor-time snapshot) - never crash the auth flow over this. */
  }
}

export function clearPersistedAccessToken(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Read the currently stored token (sessionStorage preferred, then legacy localStorage). */
export function readStoredAccessToken(): StoredAuthToken | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuthToken;
  } catch {
    return null;
  }
}
